import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@headlessui/react";
import { isHigherThanDistrictAdmin, roleDescriptions, UserRole } from "../../../app/userSlice";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import ModalFrame from "../../../components/ModalFrame";
import { EditUserViewModel, roleColors } from "../usersApiSlice";
import UserLog from "./UserLog";

interface AccountInfoProps {
	userInfo: EditUserViewModel;
}

export default function AccountInfoCard({ userInfo }: AccountInfoProps) {
	const [showPassword, setShowPassword] = useState(false);
	const [isUserLogDialogOpen, setIsUserLogDialogOpen] = useState(false);

	const navigate = useNavigate();

	const handleCloseUserLog = () => {
		setIsUserLogDialogOpen(false);
	};

	return (
		<section
			className={`${userInfo.role === UserRole.Writer ? "min-[1200px]:w-1/4" : "min-[1200px]:w-[30%]"} rounded-xl border border-gray-200 self-start`}>
			<div className="border-b border-gray-900/5 bg-gray-200/60 px-5 py-4 rounded-t-xl">
				<p className="text-xl font-medium">Account Information</p>
			</div>
			<div className="divide-y-2 bg-gray-50 rounded-b-xl">
				<div className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-4 items-baseline px-5 py-4">
					<div className="font-medium text-gray-600">Role</div>
					<div>
						<span
							className={`${roleColors[userInfo.role as UserRole]} flex shrink-0 items-center justify-center rounded-full px-4 py-1`}>
							<span className="text-sm text-center">
								{roleDescriptions[userInfo.role as UserRole]}{" "}
								{userInfo.isTeachersWriterAccount && (
									<span>(Teacher's Account)</span>
								)}
							</span>
						</span>
					</div>

					{!isHigherThanDistrictAdmin(userInfo.editorsRole) && (
						<>
							<div className="font-medium text-gray-600">Username</div>
							<div className="break-all">{userInfo.editModel.userName}</div>
						</>
					)}

					<div className="font-medium text-gray-600">Created On</div>
					<div>
						<FormattedDateAndTime date={userInfo.createDate} />
					</div>
					<div className="font-medium text-gray-600">Last Logged In</div>
					<div>
						{userInfo.lastLoginDate ? (
							<FormattedDateAndTime date={userInfo.lastLoginDate} />
						) : (
							"Never"
						)}
					</div>
				</div>
				<div className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2.5 items-baseline px-5 py-4">
					<div className="font-medium text-gray-600">User Log</div>
					<Button
						className="btn btn-action w-fit justify-self-end"
						onClick={() => setIsUserLogDialogOpen(true)}>
						View
					</Button>

					{userInfo.role === UserRole.Writer && (
						<>
							<div className="font-medium text-gray-600 col-span-full">
								Writing Summary
							</div>
							<div className="flex justify-between items-center col-span-full">
								<p className="ml-3 mr-2 text-sm">
									{userInfo.fullName} wrote {userInfo.testsTaken} drafts
								</p>
								<Button
									className="btn btn-action w-fit justify-self-end"
									onClick={() =>
										navigate(`/writing/viewDrafts?writerId=${userInfo.id}`)
									}>
									View
								</Button>
							</div>

							<div className="font-medium text-gray-600">Current Password</div>
							<Button
								className="btn btn-action w-fit justify-self-end"
								onClick={() => setShowPassword(!showPassword)}>
								{showPassword ? "Hide" : "View"}
							</Button>
							{showPassword && (
								<div className="ml-3 col-span-full text-sm">
									{userInfo.currentPassword}
								</div>
							)}
						</>
					)}
				</div>
			</div>
			<ModalFrame
				open={isUserLogDialogOpen}
				title={`User Log: ${userInfo.fullName}`}
				maxWidth="sm:max-w-[900px]"
				onClose={handleCloseUserLog}>
				<UserLog userId={userInfo.id} setIsDialogOpen={handleCloseUserLog} />
			</ModalFrame>
		</section>
	);
}
